import {IconButton, Popover, Stack} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {Asset} from "@components/asset";
import {UserAvatar} from "@components/UserAvatar";
import {useCalendarContext} from "../..";
import {Tag} from "@components";
import {translate} from "@util";
import {FilterGroup} from "./FilterGroup";
import {useDownBreakpoint} from "@hooks";
import {useThemeContext} from "@theme";

const CalendarFilter = ({disabled = false}: {disabled?: boolean}) => {
  const {tasks, filter, setFilter} = useCalendarContext();
  const {theme} = useThemeContext();

  const assetIds: string[] = Array.from(
    new Set(tasks.filter(t => t.assetId).map(t => t.assetId!)),
  );
  const userIds: string[] = Array.from(
    new Set(tasks.filter(t => t.assigneeId).map(t => t.assigneeId!)),
  );
  const tags: string[] = Array.from(
    new Set(tasks.filter(t => t.tags).flatMap(t => t.tags!)),
  );

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (tasks.length > 0) {
      setFilter(prev => ({
        ...prev,
        position: {top: e.clientY, left: e.clientX},
      }));
    }
  };

  const handleSelection = (id: string, checked: boolean) => {
    setFilter(prev => ({
      ...prev,
      excluded: checked
        ? prev.excluded.filter(e => e !== id)
        : [...prev.excluded, id],
    }));
  };

  const handleSelectAll = (ids: string[], checked: boolean) => {
    setFilter(prev => {
      const others = prev.excluded.filter(e => !ids.includes(e));
      return {...prev, excluded: checked ? others : [...others, ...ids]};
    });
  };

  const isDownSm = useDownBreakpoint("sm");
  const isChecked = (id: string) => !filter.excluded.includes(id);

  const open = Boolean(filter.position);

  return (
    <>
      <IconButton onClick={handleClick} disabled={disabled}>
        <FilterListIcon fontSize="inherit" />
      </IconButton>
      <Popover
        open={open}
        onClose={() => setFilter(prev => ({...prev, position: undefined}))}
        transitionDuration={0}
        anchorReference="anchorPosition"
        anchorPosition={filter.position}>
        <Stack
          direction={isDownSm ? "column" : "row"}
          bgcolor={"background.paper"}>
          <FilterGroup
            title={translate("common.label.asset")}
            ids={assetIds}
            isChecked={isChecked}
            count={id => tasks.filter(t => t.assetId === id).length}
            renderLabel={id => <Asset id={id} variant="body2" size="medium" />}
            onChange={handleSelection}
            onChangeAll={handleSelectAll}
          />
          <FilterGroup
            sx={{
              ...(!isDownSm && {
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
              }),
            }}
            title={translate("entity.user.title")}
            ids={userIds}
            isChecked={isChecked}
            count={id => tasks.filter(t => t.assigneeId === id).length}
            renderLabel={id => <UserAvatar id={id} full width={30} />}
            onChange={handleSelection}
            onChangeAll={handleSelectAll}
          />
          <FilterGroup
            title={translate("common.label.tag")}
            ids={tags}
            isChecked={isChecked}
            count={id => tasks.filter(t => t.tags?.includes(id)).length}
            renderLabel={id => <Tag tag={id} container="div" />}
            onChange={handleSelection}
            onChangeAll={handleSelectAll}
          />
        </Stack>
      </Popover>
    </>
  );
};

export {CalendarFilter};
